import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock3,
  FileText,
  GraduationCap,
  Headphones,
  Play,
  Star,
} from "lucide-react";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import type { TodayFavouriteCandidate, TodayResponse, TodaySession } from "@learn-flow/contracts";

import { AppShell } from "../../components/app-shell.js";
import { FlashcardIndicator } from "../../components/flashcard-indicator.js";
import { PageHeaderCreateAction } from "../../components/page-header-create-action.js";
import { createActivityReturnState } from "../../lib/activity-return-context.js";
import { ApiError } from "../../lib/api.js";
import { createUuid } from "../../lib/random-uuid.js";
import { activityDetailKey } from "../activities/activity-api.js";
import { formatMinutes, formatShortOrdinalDate } from "../calendar/calendar-date.js";
import { calendarQueryRoot } from "../calendar/calendar-api.js";
import { libraryQueryRoot } from "../library/library-api.js";
import { completeTodaySession, loadToday, todayQueryRoot } from "./today-api.js";
import { selectFavourites } from "./today-favourites.js";

type CompleteCommand = {
  type: "complete";
  session: TodaySession;
  idempotencyKey: string;
};

type ActionFailure = {
  message: string;
  retry: CompleteCommand | null;
};

type FocusTarget = {
  sessionId: string;
  action: "complete" | "view";
};

export function TodayPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const actionInFlight = useRef<CompleteCommand | null>(null);
  const [pendingCommand, setPendingCommand] = useState<CompleteCommand | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const focusTarget = useRef<FocusTarget | null>(null);
  const today = useQuery({
    queryKey: todayQueryRoot,
    queryFn: loadToday,
    refetchInterval: 60_000,
    refetchOnWindowFocus: "always",
  });
  useEffect(() => {
    document.title = "Today · MemoKite";
  }, []);

  const restoreFocus = useCallback((sessionId: string, action: "complete" | "view") => {
    focusTarget.current = { sessionId, action };
  }, []);

  useLayoutEffect(() => {
    const requestedFocus = focusTarget.current;
    if (!requestedFocus || pendingCommand) return;
    const selector = `[data-today-session="${requestedFocus.sessionId}"] [data-session-action="${requestedFocus.action}"]`;
    let timer = 0;
    let cancelled = false;
    const focusWhenReady = (attemptsRemaining: number): void => {
      if (cancelled) return;
      const target = document.querySelector<HTMLElement>(selector);
      if (target && !(target instanceof HTMLButtonElement && target.disabled)) {
        target.focus({ preventScroll: true });
        timer = window.setTimeout(() => {
          if (cancelled) return;
          if (document.activeElement === target) {
            if (focusTarget.current === requestedFocus) focusTarget.current = null;
          } else if (attemptsRemaining > 0) {
            focusWhenReady(attemptsRemaining - 1);
          }
        }, 250);
        return;
      }
      if (attemptsRemaining > 0) {
        timer = window.setTimeout(() => focusWhenReady(attemptsRemaining - 1), 0);
      }
    };
    timer = window.setTimeout(() => focusWhenReady(8), 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [announcement, failure, pendingCommand, today.data]);

  const invalidateConnectedViews = useCallback(
    async (session: TodaySession) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: todayQueryRoot }),
        queryClient.invalidateQueries({ queryKey: calendarQueryRoot }),
        queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
        queryClient.invalidateQueries({ queryKey: activityDetailKey(session.activityId) }),
      ]);
    },
    [queryClient],
  );

  const complete = useMutation({
    mutationFn: (command: CompleteCommand) =>
      completeTodaySession(command.session, {
        version: command.session.version,
        idempotencyKey: command.idempotencyKey,
      }),
    onMutate: async (command) => {
      setFailure(null);
      setAnnouncement("");
      await queryClient.cancelQueries({ queryKey: todayQueryRoot });
      const previous = queryClient.getQueryData<TodayResponse>(todayQueryRoot);
      if (previous) {
        queryClient.setQueryData(
          todayQueryRoot,
          optimisticallyComplete(previous, command.session.id),
        );
      }
      return { previous };
    },
    onSuccess: async (result, command) => {
      queryClient.setQueryData<TodayResponse>(todayQueryRoot, (current) =>
        current ? applyCompletionResult(current, command.session.id, result) : current,
      );
      setAnnouncement(`${command.session.activityTitle ?? "Session"} completed.`);
      await invalidateConnectedViews(command.session);
      restoreFocus(command.session.id, "view");
    },
    onError: (error, command, context) => {
      if (context?.previous) queryClient.setQueryData(todayQueryRoot, context.previous);
      const changed = isSessionConflict(error);
      setFailure({
        message: changed
          ? "This session changed in another view. Refresh Today before changing it again."
          : "Completion was not recorded. Nothing changed; try again.",
        retry: changed ? null : command,
      });
      if (changed) void queryClient.invalidateQueries({ queryKey: todayQueryRoot });
      restoreFocus(command.session.id, "complete");
    },
    onSettled: () => {
      actionInFlight.current = null;
      setPendingCommand(null);
    },
  });

  const submit = useCallback(
    (command: CompleteCommand) => {
      if (actionInFlight.current) return;
      actionInFlight.current = command;
      setPendingCommand(command);
      complete.mutate(command);
    },
    [complete],
  );

  if (today.isPending) {
    return (
      <TodayState>
        <TodaySkeleton />
      </TodayState>
    );
  }
  if (today.isError) {
    return (
      <TodayState>
        <section className="today-full-error" role="alert">
          <CircleAlert size={30} aria-hidden="true" />
          <h1>Today could not be loaded</h1>
          <p>Your learning plan is safe. Try loading this view again.</p>
          <button type="button" className="soft-button" onClick={() => void today.refetch()}>
            Try again
          </button>
        </section>
      </TodayState>
    );
  }

  const response = today.data;
  return (
    <AppShell mainClassName="today-screen">
      <header className="topbar today-topbar">
        <div>
          <h1>Today</h1>
        </div>
        <PageHeaderCreateAction label="New activity" />
      </header>

      {failure ? (
        <div className="today-action-error" role="alert">
          <span>{failure.message}</span>
          {failure.retry ? (
            <button
              type="button"
              className="soft-button"
              disabled={Boolean(pendingCommand)}
              onClick={() => failure.retry && submit(failure.retry)}
            >
              Try again
            </button>
          ) : (
            <button type="button" className="soft-button" onClick={() => void today.refetch()}>
              Refresh Today
            </button>
          )}
        </div>
      ) : null}

      <div className="today-dashboard">
        <TodayPlan
          response={response}
          pendingCommand={pendingCommand}
          onComplete={(session) =>
            submit({ type: "complete", session, idempotencyKey: createUuid() })
          }
        />
        <WeekPanel response={response} />
        <ComingUpPanel response={response} />
        <FavouritesPanel response={response} />
      </div>

      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
    </AppShell>
  );
}

function TodayPlan({
  response,
  pendingCommand,
  onComplete,
}: {
  response: TodayResponse;
  pendingCommand: CompleteCommand | null;
  onComplete: (session: TodaySession) => void;
}): React.JSX.Element {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const previousIndexRef = useRef(0);
  const pointerStartRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);
  const completed = response.sessions.filter((session) => session.status === "completed").length;
  const remainingMinutes = response.sessions.reduce(
    (total, session) =>
      session.status === "completed" ? total : total + (session.activityDurationMinutes ?? 0),
    0,
  );
  const selectedSessionIndex = useMemo(() => {
    if (response.sessions.length === 0) return -1;
    const matchingIndex = response.sessions.findIndex(
      (session) => session.id === selectedSessionId,
    );
    return matchingIndex >= 0
      ? matchingIndex
      : Math.min(previousIndexRef.current, response.sessions.length - 1);
  }, [response.sessions, selectedSessionId]);
  const selectedSession =
    selectedSessionIndex < 0 ? null : (response.sessions[selectedSessionIndex] ?? null);
  const hasCarousel = response.sessions.length > 1;

  useLayoutEffect(() => {
    if (selectedSessionIndex >= 0) previousIndexRef.current = selectedSessionIndex;
    if (selectedSession?.id !== selectedSessionId) {
      setSelectedSessionId(selectedSession?.id ?? null);
    }
  }, [selectedSession, selectedSessionId, selectedSessionIndex]);

  const selectSession = useCallback(
    (nextIndex: number) => {
      const nextSession = response.sessions[nextIndex];
      if (!nextSession) return;
      previousIndexRef.current = nextIndex;
      setSelectedSessionId(nextSession.id);
    },
    [response.sessions],
  );

  const moveSelection = useCallback(
    (direction: -1 | 1) => {
      selectSession(selectedSessionIndex + direction);
    },
    [selectSession, selectedSessionIndex],
  );

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary) return;
    pointerStartRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  }, []);

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const start = pointerStartRef.current;
      pointerStartRef.current = null;
      if (!start || start.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
      moveSelection(deltaX < 0 ? 1 : -1);
    },
    [moveSelection],
  );

  return (
    <section className="today-dashboard-card today-plan-panel" aria-labelledby="today-plan-heading">
      <h2 id="today-plan-heading" className="section-kicker">
        Today's plan: {formatShortOrdinalDate(response.date)}
      </h2>
      <div className="today-plan-overview">
        <p className="today-plan-summary">
          {completed} of {response.sessions.length} sessions complete
          <span aria-hidden="true">·</span>
          <span>{remainingMinutes} min remaining</span>
        </p>
        {hasCarousel ? (
          <div className="today-carousel-controls" aria-label="Today session navigation">
            <button
              type="button"
              aria-label="Previous session"
              disabled={selectedSessionIndex <= 0}
              onClick={() => moveSelection(-1)}
            >
              <ChevronLeft size={17} aria-hidden="true" />
            </button>
            <span aria-live="polite" aria-atomic="true">
              {selectedSessionIndex + 1} of {response.sessions.length}
            </span>
            <button
              type="button"
              aria-label="Next session"
              disabled={selectedSessionIndex >= response.sessions.length - 1}
              onClick={() => moveSelection(1)}
            >
              <ChevronRight size={17} aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
      <div
        className="today-plan-carousel"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => {
          pointerStartRef.current = null;
        }}
        onClickCapture={(event) => {
          if (!suppressClickRef.current) return;
          event.preventDefault();
          event.stopPropagation();
          suppressClickRef.current = false;
        }}
      >
        {selectedSession ? (
          <div
            className="today-plan-slide"
            role="group"
            aria-label={`Session ${(selectedSessionIndex + 1).toString()} of ${response.sessions.length.toString()}`}
            aria-roledescription="slide"
          >
            <TodaySessionCard
              key={selectedSession.id}
              session={selectedSession}
              index={selectedSessionIndex + 1}
              pendingAction={
                pendingCommand?.session.id === selectedSession.id ? pendingCommand.type : null
              }
              onComplete={() => onComplete(selectedSession)}
            />
          </div>
        ) : (
          <div className="today-panel-empty">Nothing is planned for today.</div>
        )}
      </div>
    </section>
  );
}

function TodaySessionCard({
  session,
  index,
  pendingAction,
  onComplete,
}: {
  session: TodaySession;
  index: number;
  pendingAction: CompleteCommand["type"] | null;
  onComplete: () => void;
}): React.JSX.Element {
  const title = session.activityTitle ?? "Activity unavailable";
  const presentedStatus = pendingAction === "complete" ? "scheduled" : session.status;
  const completed = presentedStatus === "completed";
  const canAct = session.activityAvailable;
  return (
    <article
      className={`today-session-card status-${presentedStatus}`}
      data-today-session={session.id}
      aria-label={`${title}, ${session.scheduledTime ?? "Any time"}, ${presentedStatus}`}
    >
      <div className="today-session-state" aria-hidden="true">
        {completed ? <Check size={21} /> : index}
      </div>
      <div className="today-session-main">
        <span className="today-session-meta">
          {completed ? "Completed · " : ""}
          {session.scheduledTime ?? "Any time"}
        </span>
        {canAct ? (
          <TodaySessionTitleLink session={session} title={title} />
        ) : (
          <strong>{title}</strong>
        )}
      </div>
      {session.activityDurationMinutes === null ? null : (
        <span className="today-session-duration">
          <Clock3 size={16} aria-hidden="true" />
          {formatMinutes(session.activityDurationMinutes)}
        </span>
      )}
      <div className="today-session-footer">
        <div className="today-session-taxonomy" aria-label="Activity metadata">
          <span className="today-session-category">
            {canAct ? (session.collectionName ?? "Uncollected") : "Reference missing"}
          </span>
          {canAct && session.hasFlashcards ? <FlashcardIndicator /> : null}
        </div>
        <div className="today-session-actions">
          {canAct && !completed ? (
            <>
              <Link
                className="dark-button today-start-session"
                to={`/activities/${session.activityId}?sessionId=${session.id}`}
                state={createActivityReturnState("/today")}
              >
                <Play size={15} fill="currentColor" aria-hidden="true" />
                Start session
              </Link>
              <button
                type="button"
                className="today-complete-session"
                data-session-action="complete"
                aria-disabled={pendingAction ? "true" : undefined}
                aria-label={
                  pendingAction === "complete"
                    ? `Saving completion: ${title}`
                    : `Complete session: ${title}`
                }
                onClick={pendingAction ? undefined : onComplete}
              >
                <Check size={15} aria-hidden="true" />
                {pendingAction === "complete" ? "Saving…" : "Complete"}
              </button>
            </>
          ) : null}
          {canAct && completed ? (
            <Link
              className="dark-button today-view-session"
              data-session-action="view"
              to={`/activities/${session.activityId}?sessionId=${session.id}`}
              state={createActivityReturnState("/today")}
            >
              <BookOpen size={15} aria-hidden="true" />
              View session
            </Link>
          ) : null}
          {!canAct ? <span className="today-history-label">Unavailable</span> : null}
        </div>
      </div>
    </article>
  );
}

type TooltipPosition = {
  left: number;
  maxWidth: number;
  placement: "above" | "below";
  top: number;
};

function TodaySessionTitleLink({
  session,
  title,
}: {
  session: TodaySession;
  title: string;
}): React.JSX.Element {
  const tooltipId = useId();
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);

  const positionTooltip = useCallback(() => {
    const link = linkRef.current;
    if (!link) return;
    const rect = link.getBoundingClientRect();
    const maxWidth = Math.min(360, window.innerWidth - 32);
    const left = Math.min(Math.max(16, rect.left), window.innerWidth - maxWidth - 16);
    const showBelow = rect.bottom + 96 < window.innerHeight;
    setTooltipPosition({
      left,
      maxWidth,
      placement: showBelow ? "below" : "above",
      top: showBelow ? rect.bottom + 8 : rect.top - 8,
    });
  }, []);

  useEffect(() => {
    if (!tooltipPosition) return;
    const reposition = () => positionTooltip();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [positionTooltip, tooltipPosition]);

  return (
    <>
      <Link
        ref={linkRef}
        className="today-session-title"
        to={`/activities/${session.activityId}?sessionId=${session.id}`}
        state={createActivityReturnState("/today")}
        draggable={false}
        aria-describedby={tooltipPosition ? tooltipId : undefined}
        onMouseEnter={positionTooltip}
        onMouseLeave={() => setTooltipPosition(null)}
        onFocus={positionTooltip}
        onBlur={() => setTooltipPosition(null)}
      >
        {title}
      </Link>
      {tooltipPosition
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              className={`today-session-title-tooltip is-${tooltipPosition.placement}`}
              style={{
                left: tooltipPosition.left,
                maxWidth: tooltipPosition.maxWidth,
                top: tooltipPosition.top,
              }}
            >
              {title}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

function WeekPanel({ response }: { response: TodayResponse }): React.JSX.Element {
  const maxCount = Math.max(1, ...response.week.days.map((day) => day.sessionCount));
  return (
    <section className="today-dashboard-card today-week-panel" aria-labelledby="week-heading">
      <h2 id="week-heading" className="section-kicker">
        This week
      </h2>
      <div className="today-week-chart" aria-label="Sessions planned by day this week">
        {response.week.days.map((day) => {
          const incompleteSessionCount = day.sessionCount - day.completedSessionCount;
          const completedPercent =
            day.sessionCount === 0 ? 0 : (day.completedSessionCount / day.sessionCount) * 100;
          const countOnCompleted = day.completedSessionCount * 2 >= day.sessionCount;
          const accessibleDate = formatWeekDateAccessible(day.date);
          const sessionSummary = `${day.sessionCount.toString()} sessions, ${day.completedSessionCount.toString()} completed, ${incompleteSessionCount.toString()} incomplete`;
          return (
            <div
              key={day.date}
              role="group"
              className={`today-week-day ${day.isToday ? "is-today" : ""}`}
              aria-label={`${accessibleDate}${day.isToday ? ", today" : ""}: ${sessionSummary}`}
            >
              <span className="today-week-label">{formatWeekdayShort(day.date)}</span>
              <span className="today-week-bar-track">
                {day.sessionCount > 0 ? (
                  <Link
                    className="today-week-bar"
                    to={`/calendar?start=${encodeURIComponent(day.date)}&selected=${encodeURIComponent(day.date)}`}
                    aria-label={`Open Calendar on ${accessibleDate}: ${sessionSummary}`}
                    style={{
                      height: `${((day.sessionCount / maxCount) * 100).toString()}%`,
                    }}
                  >
                    <span aria-hidden="true">
                      <span
                        className="today-week-bar-incomplete"
                        style={{ height: `${(100 - completedPercent).toString()}%` }}
                      />
                      <span
                        className="today-week-bar-completed"
                        style={{ height: `${completedPercent.toString()}%` }}
                      />
                      <span
                        className={`today-week-count ${countOnCompleted ? "is-on-completed" : ""}`}
                      >
                        {day.sessionCount}
                      </span>
                    </span>
                  </Link>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
      <div className="today-week-metrics">
        <WeekMetric
          icon={<CalendarDays size={25} aria-hidden="true" />}
          value={response.week.totalSessions}
          label="sessions"
        />
        <WeekMetric
          icon={<Check size={25} aria-hidden="true" />}
          value={response.week.completedSessions}
          label="completed"
        />
        <WeekMetric
          icon={<Clock3 size={25} aria-hidden="true" />}
          value={response.week.plannedMinutes}
          label="min planned"
        />
      </div>
    </section>
  );
}

function WeekMetric({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
}): React.JSX.Element {
  return (
    <div className="today-week-metric" aria-label={`${value.toString()} ${label}`}>
      {icon}
      <span>
        <strong>{value}</strong>
        <small>{label}</small>
      </span>
    </div>
  );
}

function ComingUpPanel({ response }: { response: TodayResponse }): React.JSX.Element {
  return (
    <section className="today-dashboard-card today-coming-panel" aria-labelledby="coming-heading">
      <div className="today-card-heading">
        <h2 id="coming-heading" className="section-kicker">
          Coming up
        </h2>
        <Link className="text-button" to="/calendar">
          Open calendar <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
      <div className="today-coming-days">
        {response.comingUp.map((day) => (
          <div className="today-coming-day" key={day.date}>
            <h3>{formatUpcomingDate(day.date)}</h3>
            <div className="today-coming-sessions">
              {day.sessions.length > 0 ? (
                day.sessions.map((session) => {
                  const copy = (
                    <>
                      <strong>{session.activityTitle ?? "Activity unavailable"}</strong>
                      <span className="today-coming-metadata">
                        <span>{session.scheduledTime ?? "Any time"}</span>
                        {session.activityDurationMinutes === null ? null : (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>{formatMinutes(session.activityDurationMinutes)}</span>
                          </>
                        )}
                        <span aria-hidden="true">·</span>
                        <small>
                          {session.activityAvailable
                            ? (session.collectionName ?? "Uncollected")
                            : "Reference missing"}
                        </small>
                        {session.activityAvailable && session.hasFlashcards ? (
                          <FlashcardIndicator />
                        ) : null}
                      </span>
                    </>
                  );
                  return session.activityAvailable ? (
                    <Link
                      key={session.id}
                      className="today-coming-session"
                      to={`/activities/${session.activityId}?sessionId=${session.id}`}
                      state={createActivityReturnState("/today")}
                    >
                      <BookOpen size={20} aria-hidden="true" />
                      <span>{copy}</span>
                    </Link>
                  ) : (
                    <div key={session.id} className="today-coming-session unavailable">
                      <BookOpen size={20} aria-hidden="true" />
                      <span>{copy}</span>
                    </div>
                  );
                })
              ) : (
                <span className="today-coming-empty">Nothing scheduled</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FavouritesPanel({ response }: { response: TodayResponse }): React.JSX.Element {
  const favourites = useMemo(
    () => selectFavourites(response.favouriteCandidates),
    [response.favouriteCandidates],
  );
  return (
    <section
      className="today-dashboard-card today-favourites-panel"
      aria-labelledby="favourites-heading"
    >
      <div className="today-card-heading">
        <h2 id="favourites-heading" className="section-kicker">
          Favourites
        </h2>
        <Link className="text-button" to="/library">
          View library <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
      {favourites.length > 0 ? (
        <div className="today-favourites-rail">
          {favourites.map((activity) => (
            <article className="today-favourite-card" key={activity.activityId}>
              <span className="today-favourite-icon" aria-hidden="true">
                {formatIcon(activity.format)}
              </span>
              <div className="today-favourite-copy">
                <h3>
                  <Link
                    className="today-favourite-title-link"
                    to={`/activities/${activity.activityId}`}
                    state={createActivityReturnState("/today")}
                  >
                    {activity.title}
                  </Link>
                </h3>
                <span className="today-favourite-metadata">
                  <span className="today-favourite-duration">
                    <Clock3 size={15} aria-hidden="true" />
                    {formatMinutes(activity.estimatedMinutes)}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span className="today-session-category">
                    {activity.collectionName ?? "Uncollected"}
                  </span>
                  {activity.hasFlashcards ? <FlashcardIndicator /> : null}
                </span>
              </div>
              <div className="today-favourite-footer">
                {activity.interest === null ? (
                  <span className="today-unrated">Unrated</span>
                ) : (
                  <span aria-label={`Interest ${activity.interest.toString()} out of 5`}>
                    <Star size={15} fill="currentColor" aria-hidden="true" />
                    {activity.interest}/5
                  </span>
                )}
                <Link
                  to={`/activities/${activity.activityId}/schedule?date=${response.date}`}
                  state={createActivityReturnState("/today")}
                >
                  Plan <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="today-panel-empty">No activities in your library yet.</div>
      )}
    </section>
  );
}

function TodayState({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <AppShell mainClassName="today-screen">{children}</AppShell>;
}

function TodaySkeleton(): React.JSX.Element {
  return (
    <div className="today-skeleton" aria-busy="true" aria-label="Loading Today">
      <div className="today-skeleton-heading" />
      <div className="today-skeleton-dashboard">
        <div />
        <div />
        <div />
        <div />
      </div>
    </div>
  );
}

function formatWeekdayShort(date: string): string {
  return new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: "UTC" })
    .format(new Date(`${date}T12:00:00.000Z`))
    .toLocaleUpperCase("en-US");
}

function formatWeekDateAccessible(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00.000Z`));
}

function formatUpcomingDate(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  })
    .format(new Date(`${date}T12:00:00.000Z`))
    .toLocaleUpperCase("en-US");
}

function formatIcon(format: TodayFavouriteCandidate["format"]): React.ReactNode {
  if (format === "podcast") return <Headphones size={24} />;
  if (format === "course" || format === "practice") return <GraduationCap size={24} />;
  if (format === "article") return <FileText size={24} />;
  if (format === "video") return <Play size={24} />;
  return <BookOpen size={24} />;
}

function optimisticallyComplete(response: TodayResponse, sessionId: string): TodayResponse {
  const target = response.sessions.find((session) => session.id === sessionId);
  const alreadyCompleted = target?.status === "completed";
  return {
    ...response,
    sessions: response.sessions.map((session) =>
      session.id === sessionId
        ? {
            ...session,
            status: "completed",
            completedAt: new Date().toISOString(),
            version: session.version + 1,
          }
        : session,
    ),
    week: {
      ...response.week,
      completedSessions:
        target && !alreadyCompleted
          ? response.week.completedSessions + 1
          : response.week.completedSessions,
      days:
        target && !alreadyCompleted
          ? changeDayCompletedCount(response, target.scheduledDate, 1)
          : response.week.days,
    },
  };
}

function changeDayCompletedCount(
  response: TodayResponse,
  scheduledDate: string,
  delta: -1 | 1,
): TodayResponse["week"]["days"] {
  return response.week.days.map((day) =>
    day.date === scheduledDate
      ? {
          ...day,
          completedSessionCount: Math.min(
            day.sessionCount,
            Math.max(0, day.completedSessionCount + delta),
          ),
        }
      : day,
  );
}

function applyCompletionResult(
  response: TodayResponse,
  sessionId: string,
  completion: Awaited<ReturnType<typeof completeTodaySession>>,
): TodayResponse {
  return {
    ...response,
    sessions: response.sessions.map((session) => {
      if (session.id !== sessionId) return session;
      return {
        ...session,
        ...completion.session,
        completionId: completion.completion.id,
        completedSessionCount: session.completedSessionCount + (session.completionId ? 0 : 1),
      };
    }),
  };
}

function isSessionConflict(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    ["SESSION_VERSION_CONFLICT", "SESSION_NOT_FOUND", "SESSION_TRANSITION_INVALID"].includes(
      error.problem?.code ?? "",
    )
  );
}
